import React, { useState, useMemo, useEffect } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { Card, Typography } from "@material-ui/core";
import _ from "lodash";

import {
  TableActionTypes,
  IAction,
  IColumn,
  Dictionary,
  TFilter,
  TFilterWidget
} from "./interfaces";
import { Icon } from "../icons/Icon";
import { Button } from "../../components";

const useStyles = makeStyles(theme => ({
  root: {
    position: "relative"
  },
  buttonRow: {
    display: "flex",
    justifyContent: "center",
    marginBottom: theme.spacing(1),
    marginTop: theme.spacing(2),
    width: "100%"
  },
  filterContent: {
    display: "flex",
    flexFlow: "row wrap",
    justifyContent: "flex-start",
    marginTop: theme.spacing(2),
    width: "350px",
    "&>div": {
      paddingRight: "8px"
    }
  },
  filterIcon: {
    marginRight: theme.spacing(1),
    zIndex: 3
  },
  filterMenu: {
    padding: theme.spacing(2),
    position: "absolute",
    right: 0,
    top: "60px",
    overflow: "visible",
    zIndex: 1
  },
  submit: {
    marginRight: theme.spacing(2)
  },
  select: {
    marginBottom: theme.spacing(1)
  }
}));

type option = {
  label: string;
  value: any;
};

type Props = {
  id?: string;
  data: any[];
  columns: IColumn[];
  filters: TFilter[];
  dispatch: (action: IAction) => void;
};

export const FilterBar: React.FC<Props> = ({
  id,
  data,
  filters,
  columns,
  dispatch
}) => {
  const classes = useStyles({});
  const [open, setOpen] = useState(false);

  useEffect(() => {
    setOpen(false);
  }, [id]);

  const filterOptions = useMemo<Dictionary<option[]>>(() => {
    const filterableColumns = columns.filter(column => column.filterWidget);

    const parsedTableData = data.map(row => {
      return filterableColumns.reduce(
        (option, column) => {
          option[column.id] = {
            label: !column.parse
              ? row[column.id]
              : column.parse(row[column.id]),
            value: row[column.id]
          };
          return option;
        },
        {} as any
      );
    });

    return filterableColumns.reduce(
      (result, column) => {
        result[column.id] = _.uniqBy(
          parsedTableData.map(row => row[column.id]),
          "label"
        ).filter(option => ![undefined, null, ''].includes(option.label)).sort(function(a, b) {
          var labelA = a.label.toUpperCase();
          var labelB = b.label.toUpperCase();
          if (labelA < labelB) {
            return -1;
          }
          if (labelA > labelB) {
            return 1;
          }
          return 0;
        });

        return result;
      },
      {} as any
    );
  }, [data, columns]);

  const filterDictionary = filters.reduce<Dictionary<any>>(
    (dictionary, filter) => {
      dictionary[filter.id] = filter.value;
      return dictionary;
    },
    {}
  );

  if (columns.length === 0) return null;

  return (
    <div className={classes.root}>
      <Button
        id="filter"
        onClick={() => {
          setOpen(!open);
        }}
      >
        <Icon
          className={classes.filterIcon}
          name={open ? "close" : "filter_list"}
        />
        Filter
      </Button>
      {open && (
        <Card className={classes.filterMenu}>
          <Typography variant="h5">Filter</Typography>
          <div className={classes.filterContent}>
            {columns
              .filter(column => column.filterWidget)
              .map(column =>
                (column.filterWidget as TFilterWidget)(
                  filterDictionary[column.id],
                  payload => {
                    dispatch({
                      name: column.id,
                      type: TableActionTypes.onFilter,
                      payload
                    });
                  },
                  { suggestions: filterOptions[column.id] }
                )
              )}
          </div>
          <div className={classes.buttonRow}>
            <div>
              <Button
                id="submit"
                className={classes.submit}
                color="primary"
                onClick={() => {
                  dispatch({
                    type: TableActionTypes.execFilter
                  });
                  setOpen(false);
                }}
              >
                Submit
              </Button>
              <Button
                id="reset"
                onClick={() => {
                  dispatch({
                    type: TableActionTypes.cleanFilter
                  });
                }}
              >
                Reset
              </Button>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
};
